import React from 'react';
import Image from 'next/image';
import { Heart, HeartBlue } from '@/assets/icons';
import { Button } from '@/components/common/Button';
import { useSidePanel } from '~context/SidePanelContext';
import { TradeBottomSheet } from '@/components/features/securities/TradeBottomSheet/TradeBottomSheet';
import useUserOwnsSecurity from '@fintronners/react-widgets/src/hooks/trades/useUserOwnsSecurity';
import { TradeSide } from '@/components/common/SidePanels/TradeSidePanel/schema';
import useWatchlist from '@/hooks/api/useWatchlist';
import { useTranslation } from 'react-i18next';

interface AssetActionsProps {
  securityId: string;
  isFixedIncome?: boolean;
  buyDisabled?: boolean;
}

const AssetActions: React.FC<AssetActionsProps> = ({
  securityId,
  isFixedIncome,
  buyDisabled,
}) => {
  const { t } = useTranslation();

  const { ownsSecurity } = useUserOwnsSecurity({ securityId });

  const { openSidePanel } = useSidePanel();

  const handleOpenTradeSidePanel = (isSell?: boolean) => {
    openSidePanel(isFixedIncome ? 'FixedIncomeTradeSidePanel' : 'Trade', {
      side: isSell ? TradeSide.SELL : TradeSide.BUY,
    });
  };

  const { assetIsInWatchlist, addToWatchlist, removeFromWatchlist } = useWatchlist(securityId);

  const onClickWatchlistButton = () => {
    if (assetIsInWatchlist) {
      removeFromWatchlist();
    } else {
      addToWatchlist();
    }
  };

  return (
    <div className="flex gap-[10px]">
      <Button
        variant={`${assetIsInWatchlist ? 'primary' : 'secondary'}`}
        size="icon"
        onClick={onClickWatchlistButton}
      >
        <Image
          alt="Heart icon"
          width={18}
          height={18}
          src={assetIsInWatchlist ? Heart.src : HeartBlue.src}
        />
      </Button>
      <TradeBottomSheet
        ownsSecurity={Boolean(ownsSecurity)}
        buyDisabled={buyDisabled}
        onSelectOption={(isSell) => handleOpenTradeSidePanel(isSell)}
      />
      <div className="hidden lg:flex lg:gap-[10px]">
        <Button
          variant="primary"
          size="lg"
          disabled={buyDisabled}
          onClick={() => handleOpenTradeSidePanel()}
        >
          {t('common.buy')}
        </Button>
        <Button
          variant="secondary"
          disabled={!ownsSecurity}
          size="lg"
          onClick={() => handleOpenTradeSidePanel(true)}
        >
          {t('common.sell')}
        </Button>
      </div>
    </div>
  );
};

export default AssetActions;
